/**
 * Asset Field Type Helpers
 * Utilities for working with organization-type-specific asset field types
 */

import { getOrganizationTypeConfig, AssetFieldType } from '@/services/organizationTypeConfig';

/**
 * Get all available field types (default + custom) for an organization type
 */
export function getAvailableFieldTypes(
  organizationType: string | null | undefined
): AssetFieldType[] {
  const config = getOrganizationTypeConfig(organizationType);
  const fieldTypes = config.assetFieldTypes;
  
  if (!fieldTypes) {
    // Fallback to default field types if not configured
    return [
      { type: 'text', label: 'Text', component: 'TextInput' },
      { type: 'number', label: 'Number', component: 'NumberInput' },
      { type: 'date', label: 'Date', component: 'DateInput' },
      { type: 'email', label: 'Email', component: 'EmailInput' },
      { type: 'tel', label: 'Phone', component: 'TelInput' },
      { type: 'textarea', label: 'Text Area', component: 'TextArea' },
    ];
  }
  
  return [
    ...(fieldTypes.default || []),
    ...(fieldTypes.custom || [])
  ];
}

/**
 * Get field type configuration by type identifier
 */
export function getFieldTypeConfig(
  fieldType: string,
  organizationType: string | null | undefined
): AssetFieldType | undefined {
  const availableTypes = getAvailableFieldTypes(organizationType);
  return availableTypes.find(ft => ft.type === fieldType);
}

/**
 * Check if a field type is a user picker type (requires role filtering)
 */
export function isUserPickerFieldType(fieldType: string): boolean {
  return fieldType.includes('_picker') || fieldType === 'user_picker';
}

/**
 * Get role filter for a user picker field type
 */
export function getRoleFilterForFieldType(
  fieldType: string,
  organizationType: string | null | undefined
): string | undefined {
  const fieldTypeConfig = getFieldTypeConfig(fieldType, organizationType);
  return fieldTypeConfig?.roleFilter;
}

